import { motion } from 'framer-motion'
import { BarChart3, Activity, Database, Key } from 'lucide-react'
import { PageHeader, useAuthStore, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { useBillingSummary, useUsageBreakdown } from '@/hooks/useBilling'

export default function Usage() {
  const { user } = useAuthStore()
  const tenantId = user?.tenant_id || ''
  const { data: summary, isLoading: summaryLoading } = useBillingSummary(tenantId)
  const { data: usage, isLoading: usageLoading } = useUsageBreakdown(tenantId)

  const breadcrumbs = buildBreadcrumbs('/settings/account/usage')

  const statCards = [
    {
      label: 'Profiles Used',
      value: summary ? `${summary.profiles_used ?? 0} / ${summary.max_profiles ?? '∞'}` : '—',
      icon: Database,
      pct: summary?.max_profiles ? ((summary.profiles_used ?? 0) / summary.max_profiles) * 100 : 0,
    },
    {
      label: 'API Keys',
      value: summary ? `${summary.api_keys_used ?? 0} / ${summary.max_api_keys ?? '∞'}` : '—',
      icon: Key,
      pct: summary?.max_api_keys ? ((summary.api_keys_used ?? 0) / summary.max_api_keys) * 100 : 0,
    },
    {
      label: 'Current Plan',
      value: summary?.plan ? summary.plan.charAt(0).toUpperCase() + summary.plan.slice(1) : '—',
      icon: Activity,
      pct: null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Usage Dashboard"
        description="Monitor your platform usage, API consumption, and resource utilization."
        breadcrumbs={breadcrumbs}
      />

      <div className="space-y-6 max-w-5xl">
        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {statCards.map((card) => (
            <motion.div
              key={card.label}
              variants={fadeUp} initial="hidden" animate="visible"
              className="rounded-xl border border-warm-200 bg-white p-5"
            >
              <div className="flex items-center gap-2 mb-3">
                <card.icon className="w-4 h-4 text-copper-500" />
                <span className="text-xs text-warm-500 uppercase tracking-wide">{card.label}</span>
              </div>
              <p className="text-xl font-heading font-bold text-midnight-800">
                {summaryLoading ? <span className="animate-pulse bg-warm-100 rounded h-6 w-20 inline-block" /> : card.value}
              </p>
              {card.pct !== null && (
                <div className="mt-3 h-1.5 bg-warm-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      card.pct > 80 ? 'bg-red-500' : card.pct > 60 ? 'bg-amber-500' : 'bg-copper-500'
                    }`}
                    style={{ width: `${Math.min(card.pct, 100)}%` }}
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Detailed Usage */}
        {usage && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-copper-500" />
              </div>
              <h2 className="font-heading font-semibold text-midnight-800">Detailed Breakdown</h2>
            </div>
            <div className="space-y-4">
              {[
                { label: 'Profiles', metric: usage.profiles },
                { label: 'API Keys', metric: usage.api_keys },
                { label: 'Rate Limit', metric: usage.rate_limit },
              ].map(({ label, metric }) => (
                <div key={label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-warm-600">{label}</span>
                    <span className="text-midnight-800 font-medium">{metric.current} / {metric.limit}</span>
                  </div>
                  <div className="h-2 bg-warm-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-copper-500 rounded-full transition-all"
                      style={{ width: `${Math.min(metric.percentage, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
              {usageLoading && (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-warm-100 rounded w-full" />
                  <div className="h-4 bg-warm-100 rounded w-3/4" />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
